"use client";

import { useEffect, useRef, memo, useState, useMemo } from "react";
import {
   createChart,
   ColorType,
   CandlestickData,
   Time,
   LineData
} from "lightweight-charts";
import { useTranslations } from "next-intl";
import { Button } from "./button";
import { cn } from "@/app/lib/utils";
import type {
   ChartDataPoint,
   Timeframe,
   TradingViewChartProps,
   ChartType
} from "@/types";
import { api_url } from "@/app/api";
import { calculateTrendChannel, isBitcoinDominance } from "@/app/lib/agents/utils";

function aggregateByTimeframe(
   data: ChartDataPoint[],
   timeframe: Timeframe
): ChartDataPoint[] {
   if (!data || data.length === 0) return data;
   if (timeframe === "1D") {
      return data;
   }

   const sortedData = [...data].sort((a, b) => a.unixTime - b.unixTime);
   const grouped: Record<number, ChartDataPoint> = {};

   sortedData.forEach((candle) => {
      const date = new Date(candle.unixTime * 1000);
      let key: number;

      // Buckets are cut in UTC: the feed is UTC, so grouping by local calendar shifts every
      // boundary by the viewer's offset and silently produces different candles per timezone.
      if (timeframe === "1W") {
         const day = date.getUTCDay();
         const monday = date.getUTCDate() - day + (day === 0 ? -6 : 1);
         key = Math.floor(
            Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), monday) / 1000
         );
      } else if (timeframe === "1M") {
         key = Math.floor(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1) / 1000);
      } else if (timeframe === "1Y") {
         key = Math.floor(Date.UTC(date.getUTCFullYear(), 0, 1) / 1000);
      } else {
         return;
      }

      if (!grouped[key]) {
         grouped[key] = {
            timestamp: candle.timestamp,
            unixTime: key,
            open: candle.open,
            high: candle.high,
            low: candle.low,
            close: candle.close,
            volume: candle.volume || 0
         };
      } else {
         grouped[key].high = Math.max(grouped[key].high, candle.high);
         grouped[key].low = Math.min(grouped[key].low, candle.low);
         grouped[key].close = candle.close;
         grouped[key].volume = (grouped[key].volume || 0) + (candle.volume || 0);
      }
   });

   return Object.values(grouped).sort((a, b) => a.unixTime - b.unixTime);
}

const TradingViewChart = memo(function TradingViewChart({
   data,
   apiUrl,
   symbol,
   name,
   description,
   height = 400,
   className = "",
   chartType
}: TradingViewChartProps) {
   const t = useTranslations("Components.TradingViewChart");

   // Auto-detect chart type if not provided
   const isBTCDominance = isBitcoinDominance(symbol) || isBitcoinDominance(name);
   const detectedChartType: ChartType =
      chartType || (isBTCDominance ? "line" : "candlestick");

   const chartContainerRef = useRef<HTMLDivElement>(null);
   const chartRef = useRef<ReturnType<typeof createChart> | null>(null);
   const candlestickSeriesRef = useRef<any>(null);
   const lineSeriesRef = useRef<any>(null);
   const priceLinesRef = useRef<any[]>([]);
   const topLineSeriesRef = useRef<any>(null);
   const bottomLineSeriesRef = useRef<any>(null);
   const mid1LineSeriesRef = useRef<any>(null);
   const mid2LineSeriesRef = useRef<any>(null);
   const topAreaTopRef = useRef<any>(null);
   const topAreaBottomRef = useRef<any>(null);
   const middleAreaTopRef = useRef<any>(null);
   const middleAreaBottomRef = useRef<any>(null);
   const bottomAreaTopRef = useRef<any>(null);
   const bottomAreaBottomRef = useRef<any>(null);
   const [timeframe, setTimeframe] = useState<Timeframe>("1W");
   const [chartData, setChartData] = useState<ChartDataPoint[]>(data || []);
   const [isLoading, setIsLoading] = useState(false);
   const [isChartReady, setIsChartReady] = useState(false);
   const [showTrendChannel, setShowTrendChannel] = useState(!isBTCDominance);

   useEffect(() => {
      if (!apiUrl) {
         if (data && data.length > 0) {
            setChartData(data);
         }
         return;
      }

      setIsLoading(true);
      const proxyUrl = `${api_url.chart_data_url}?url=${encodeURIComponent(apiUrl)}`;

      let isCancelled = false;

      fetch(proxyUrl)
         .then((res) => {
            if (!res.ok) {
               if (res.status === 404) {
                  throw new Error(
                     "Chart data API route not found. Please restart the dev server."
                  );
               }
               const contentType = res.headers.get("content-type");
               if (contentType && contentType.includes("application/json")) {
                  return res.json().then((err) => {
                     throw new Error(err.error || `HTTP error! status: ${res.status}`);
                  });
               } else {
                  throw new Error(
                     `HTTP error! status: ${res.status} - ${res.statusText}`
                  );
               }
            }
            return res.json();
         })
         .then((response: any) => {
            if (isCancelled) return;

            let apiData: any[] = [];

            if (Array.isArray(response)) {
               apiData = response;
            } else if (response.data && Array.isArray(response.data)) {
               apiData = response.data;
            } else if (response.error) {
               throw new Error(response.error);
            } else {
               throw new Error("Unexpected response format from API");
            }

            if (!apiData || apiData.length === 0) {
               throw new Error("No data received from API");
            }

            const mappedData: ChartDataPoint[] = apiData.map((item: any) => {
               if (!item.time && !item.timeDT) {
               }

               return {
                  timestamp: item.timeDT
                     ? new Date(item.timeDT).toISOString()
                     : new Date(item.time * 1000).toISOString(),
                  unixTime:
                     item.time ||
                     Math.floor(
                        new Date(item.timeDT || item.time * 1000).getTime() / 1000
                     ),
                  open: item.open,
                  high: item.high,
                  low: item.low,
                  close: item.close,
                  volume: item.volume
               };
            });

            if (!isCancelled) {
               setChartData(mappedData);
               setIsLoading(false);
            }
         })
         .catch((error) => {
            if (isCancelled) return;

            if (data && data.length > 0) {
               setChartData(data);
            }
            setIsLoading(false);
         });

      return () => {
         isCancelled = true;
      };
   }, [apiUrl]);

   const aggregatedData = useMemo(() => {
      if (!chartData || chartData.length === 0) {
         return [];
      }
      const aggregated = aggregateByTimeframe(chartData, timeframe);
      return aggregated;
   }, [chartData, timeframe]);

   // Initialize chart (only once)
   useEffect(() => {
      if (!chartContainerRef.current) return;
      const chart = createChart(chartContainerRef.current, {
         layout: {
            background: { type: ColorType.Solid, color: "#1a1a2e" },
            textColor: "#d1d5db"
         },
         grid: {
            vertLines: { color: "#2d2d44" },
            horzLines: { color: "#2d2d44" }
         },
         width: chartContainerRef.current.clientWidth,
         height: height,
         timeScale: {
            borderColor: "#3d3d5c",
            timeVisible: true,
            secondsVisible: false
         },
         rightPriceScale: {
            borderColor: "#3d3d5c"
         },
         crosshair: {
            mode: 1,
            vertLine: {
               color: "#6366f1",
               width: 1,
               style: 2,
               labelBackgroundColor: "#6366f1"
            },
            horzLine: {
               color: "#6366f1",
               width: 1,
               style: 2,
               labelBackgroundColor: "#6366f1"
            }
         }
      });

      chartRef.current = chart;

      // Initialize area series for zone backgrounds with opacity FIRST
      // This ensures they render behind candlestick and line series
      // We'll use 2 area series per zone: one from top line to 0, one from bottom line to 0
      // Overlapping them creates the area between two lines

      // Top zone: red background from mid1Line to topLine
      // Top area: from topLine to 0 (red)
      const topAreaTop = chart.addAreaSeries({
         lineColor: "rgba(239, 68, 68, 0)",
         topColor: "rgba(239, 68, 68, 0.25)",
         bottomColor: "rgba(239, 68, 68, 0)",
         priceLineVisible: false,
         lastValueVisible: false
      });
      topAreaTopRef.current = topAreaTop;

      // Bottom area: from mid1Line to 0 (same color as chart background to mask)
      const topAreaBottom = chart.addAreaSeries({
         lineColor: "#1a1a2e",
         topColor: "#1a1a2e",
         bottomColor: "#1a1a2e",
         priceLineVisible: false,
         lastValueVisible: false
      });
      topAreaBottomRef.current = topAreaBottom;

      // Middle zone: gray background from mid2Line to mid1Line
      // Top area: from mid1Line to 0 (gray)
      const middleAreaTop = chart.addAreaSeries({
         lineColor: "rgba(156, 163, 175, 0)",
         topColor: "rgba(156, 163, 175, 0.2)",
         bottomColor: "rgba(156, 163, 175, 0)",
         priceLineVisible: false,
         lastValueVisible: false
      });
      middleAreaTopRef.current = middleAreaTop;

      // Bottom area: from mid2Line to 0 (same color as chart background to mask)
      const middleAreaBottom = chart.addAreaSeries({
         lineColor: "#1a1a2e",
         topColor: "#1a1a2e",
         bottomColor: "#1a1a2e",
         priceLineVisible: false,
         lastValueVisible: false
      });
      middleAreaBottomRef.current = middleAreaBottom;

      // Bottom zone: green background from bottomLine to mid2Line
      // Top area: from mid2Line to 0 (green)
      const bottomAreaTop = chart.addAreaSeries({
         lineColor: "rgba(34, 197, 94, 0)",
         topColor: "rgba(34, 197, 94, 0.25)",
         bottomColor: "rgba(34, 197, 94, 0)",
         priceLineVisible: false,
         lastValueVisible: false
      });
      bottomAreaTopRef.current = bottomAreaTop;

      // Bottom area: from bottomLine to 0 (same color as chart background to mask)
      const bottomAreaBottom = chart.addAreaSeries({
         lineColor: "#1a1a2e",
         topColor: "#1a1a2e",
         bottomColor: "#1a1a2e",
         priceLineVisible: false,
         lastValueVisible: false
      });
      bottomAreaBottomRef.current = bottomAreaBottom;

      // Add main series (candlestick or line) AFTER area series so it renders on top
      if (detectedChartType === "line") {
         const lineSeries = chart.addLineSeries({
            color: "#3b82f6",
            lineWidth: 2,
            priceLineVisible: false,
            lastValueVisible: true
         });
         lineSeriesRef.current = lineSeries;
      } else {
         const candlestickSeries = chart.addCandlestickSeries({
            upColor: "#22c55e",
            downColor: "#ef4444",
            borderDownColor: "#ef4444",
            borderUpColor: "#22c55e",
            wickDownColor: "#ef4444",
            wickUpColor: "#22c55e"
         });
         candlestickSeriesRef.current = candlestickSeries;
      }

      // Initialize trend channel line series AFTER candlestick so they render on top
      const topLineSeries = chart.addLineSeries({
         color: "#ef4444",
         lineWidth: 1,
         // title: "Upper Cut",
         priceLineVisible: false,
         lastValueVisible: false
      });
      topLineSeriesRef.current = topLineSeries;

      const bottomLineSeries = chart.addLineSeries({
         color: "#22c55e",
         lineWidth: 1,
         // title: "Lower Cut",
         priceLineVisible: false,
         lastValueVisible: false
      });
      bottomLineSeriesRef.current = bottomLineSeries;

      const mid1LineSeries = chart.addLineSeries({
         color: "#9ca3af",
         lineWidth: 1,
         // title: "Middle",
         priceLineVisible: false,
         lastValueVisible: false
      });
      mid1LineSeriesRef.current = mid1LineSeries;

      const mid2LineSeries = chart.addLineSeries({
         color: "#9ca3af",
         lineWidth: 1,
         // title: "Middle",
         priceLineVisible: false,
         lastValueVisible: false
      });
      mid2LineSeriesRef.current = mid2LineSeries;

      setIsChartReady(true);

      const handleResize = () => {
         if (chartContainerRef.current && chartRef.current) {
            chartRef.current.applyOptions({
               width: chartContainerRef.current.clientWidth
            });
         }
      };

      window.addEventListener("resize", handleResize);

      return () => {
         window.removeEventListener("resize", handleResize);
         if (chartRef.current) {
            chartRef.current.remove();
            chartRef.current = null;
         }
         candlestickSeriesRef.current = null;
         lineSeriesRef.current = null;
         priceLinesRef.current = [];
         topLineSeriesRef.current = null;
         bottomLineSeriesRef.current = null;
         mid1LineSeriesRef.current = null;
         mid2LineSeriesRef.current = null;
         topAreaTopRef.current = null;
         topAreaBottomRef.current = null;
         middleAreaTopRef.current = null;
         middleAreaBottomRef.current = null;
         bottomAreaTopRef.current = null;
         bottomAreaBottomRef.current = null;
      };
   }, [height, detectedChartType]);

   useEffect(() => {
      const mainSeries =
         detectedChartType === "line"
            ? lineSeriesRef.current
            : candlestickSeriesRef.current;

      const isReady =
         !isChartReady ||
         !chartRef.current ||
         !mainSeries ||
         !aggregatedData ||
         aggregatedData.length === 0;
      if (isReady) return;

      if (detectedChartType === "line") {
         // For line chart, use close price
         const lineData: LineData<Time>[] = aggregatedData
            .map((point) => {
               const timeValue =
                  point.unixTime ||
                  Math.floor(new Date(point.timestamp).getTime() / 1000);
               return {
                  time: timeValue as Time,
                  value: point.close
               };
            })
            .sort((a, b) => (a.time as number) - (b.time as number));

         lineSeriesRef.current.setData(lineData);
      } else {
         // For candlestick chart, use OHLC data
         const chartData: CandlestickData<Time>[] = aggregatedData
            .map((point) => {
               const timeValue =
                  point.unixTime ||
                  Math.floor(new Date(point.timestamp).getTime() / 1000);
               return {
                  time: timeValue as Time,
                  open: point.open,
                  high: point.high,
                  low: point.low,
                  close: point.close
               };
            })
            .sort((a, b) => (a.time as number) - (b.time as number));

         candlestickSeriesRef.current.setData(chartData);
      }

      // Calculate and draw trend channel
      const trendChannel = calculateTrendChannel(aggregatedData);

      if (trendChannel && showTrendChannel) {
         // Helper function to extend lines left and right
         // Extend significantly to ensure lines are always visible when zooming
         const extendLine = (
            lineData: { time: number; value: number }[],
            firstTime: number,
            lastTime: number
         ): LineData<Time>[] => {
            if (lineData.length < 2) return [];

            const firstValue = lineData[0].value;
            const lastValue = lineData[lineData.length - 1].value;
            const dataLength = lineData.length;
            const slope = (lastValue - firstValue) / (dataLength - 1);

            // Calculate average time step (in seconds)
            const timeStep = (lastTime - firstTime) / (dataLength - 1);
            const dataTimeRange = lastTime - firstTime;

            // Extend left significantly (200% of data range) to ensure visibility when zooming
            // This ensures lines extend far enough to always be visible
            const extendLeftTimeRange = dataTimeRange * 2;
            const extendLeftCount = Math.max(
               100,
               Math.ceil(extendLeftTimeRange / timeStep)
            );
            const leftExtended: LineData<Time>[] = [];
            for (let i = extendLeftCount; i > 0; i--) {
               const extendedTime = firstTime - i * timeStep;
               const extendedValue = firstValue - slope * i;
               leftExtended.push({
                  time: extendedTime as Time,
                  value: extendedValue
               });
            }

            // Extend right significantly (200% of data range) to ensure visibility when zooming
            const extendRightTimeRange = dataTimeRange * 2;
            const extendRightCount = Math.max(
               100,
               Math.ceil(extendRightTimeRange / timeStep)
            );
            const rightExtended: LineData<Time>[] = [];
            for (let i = 1; i <= extendRightCount; i++) {
               const extendedTime = lastTime + i * timeStep;
               const extendedValue = lastValue + slope * i;
               rightExtended.push({
                  time: extendedTime as Time,
                  value: extendedValue
               });
            }

            // Combine: left extended + original + right extended
            const original: LineData<Time>[] = lineData.map((point) => ({
               time: point.time as Time,
               value: point.value
            }));

            return [...leftExtended, ...original, ...rightExtended];
         };

         const firstTime = trendChannel.topLine[0].time;
         const lastTime = trendChannel.topLine[trendChannel.topLine.length - 1].time;

         const topLineData = extendLine(trendChannel.topLine, firstTime, lastTime);
         const bottomLineData = extendLine(trendChannel.bottomLine, firstTime, lastTime);
         const mid1LineData = extendLine(trendChannel.mid1Line, firstTime, lastTime);
         const mid2LineData = extendLine(trendChannel.mid2Line, firstTime, lastTime);

         const rails = [
            [topLineSeriesRef, topLineData],
            [bottomLineSeriesRef, bottomLineData],
            [mid1LineSeriesRef, mid1LineData],
            [mid2LineSeriesRef, mid2LineData]
         ] as const;

         rails.forEach(([seriesRef, data]) => {
            if (!seriesRef.current) return;
            seriesRef.current.setData(data);
            seriesRef.current.applyOptions({ visible: true });
         });

         // Each shaded band is two stacked area series: the upper one fills its zone down
         // to the baseline, the lower one repaints the chart background over everything
         // beneath its own edge. Dropping either half floods the whole pane with colour.
         const bands = [
            [topAreaTopRef, topAreaBottomRef, topLineData, mid1LineData],
            [middleAreaTopRef, middleAreaBottomRef, mid1LineData, mid2LineData],
            [bottomAreaTopRef, bottomAreaBottomRef, mid2LineData, bottomLineData]
         ] as const;

         bands.forEach(([upperRef, lowerRef, upperData, lowerData]) => {
            if (!upperRef.current || !lowerRef.current) return;

            upperRef.current.setData(upperData);
            lowerRef.current.setData(lowerData);
            [upperRef, lowerRef].forEach((seriesRef) =>
               seriesRef.current.applyOptions({
                  visible: true,
                  baseLineVisible: false,
                  priceLineVisible: false
               })
            );
         });
      } else {
         [
            topLineSeriesRef,
            bottomLineSeriesRef,
            mid1LineSeriesRef,
            mid2LineSeriesRef,
            topAreaTopRef,
            topAreaBottomRef,
            middleAreaTopRef,
            middleAreaBottomRef,
            bottomAreaTopRef,
            bottomAreaBottomRef
         ].forEach((seriesRef) => seriesRef.current?.applyOptions({ visible: false }));
      }

      // Set default viewport to show at least 5 years of weekly candles
      // Set viewport when data is loaded or timeframe changes
      if (aggregatedData.length > 0 && chartRef.current) {
         const timeScale = chartRef.current.timeScale();

         // Calculate visible range: show at least 5 years of weekly candles
         // 5 years = 5 * 52 weeks = 260 weekly candles
         const fiveYearsInWeeks = 5 * 52; // 260 candles
         const lastCandleIndex = aggregatedData.length - 1;

         // If we have more than 5 years of data, show last 5 years
         // Otherwise, show all available data
         const visibleCandles = Math.min(fiveYearsInWeeks, aggregatedData.length);
         const firstVisibleIndex = Math.max(0, lastCandleIndex - visibleCandles + 1);

         const firstTimeValue =
            aggregatedData[firstVisibleIndex].unixTime ||
            Math.floor(
               new Date(aggregatedData[firstVisibleIndex].timestamp).getTime() / 1000
            );
         const lastTimeValue =
            aggregatedData[lastCandleIndex].unixTime ||
            Math.floor(
               new Date(aggregatedData[lastCandleIndex].timestamp).getTime() / 1000
            );

         const fromTime = firstTimeValue as Time;
         const toTime = lastTimeValue as Time;

         // Set visible range to show at least 5 years of weekly candles
         // This ensures default view shows sufficient historical context
         timeScale.setVisibleRange({
            from: fromTime,
            to: toTime
         });
      }
   }, [timeframe, aggregatedData, isChartReady, showTrendChannel, detectedChartType]);

   const timeframes: Timeframe[] = ["1D", "1W", "1M", "1Y"];

   return (
      <div className={`rounded-lg overflow-hidden border border-gray-700 ${className}`}>
         {/* Header */}
         <div className="bg-gray-800 px-3 py-1 border-b border-gray-700">
            <div className="flex flex-col gap-2">
               {/* Title and Timeframe */}
               <div className="flex flex-wrap items-center justify-between gap-1">
                  <div className="flex-1 min-w-0">
                     <h3 className="text-base font-semibold text-white truncate">
                        {name}
                     </h3>
                  </div>
                  <div className="flex items-center gap-1.5 flex-shrink-0">
                     <span className="text-xs text-gray-400">{t("timeframe")}</span>
                     <div className="flex gap-0.5">
                        {timeframes.map((tf) => (
                           <Button
                              key={tf}
                              variant={timeframe === tf ? "default" : "outline"}
                              size="sm"
                              onClick={() => setTimeframe(tf)}
                              className={cn(
                                 "h-6 px-2 text-xs font-medium",
                                 timeframe === tf
                                    ? "bg-primary text-primary-foreground"
                                    : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                              )}
                           >
                              {tf}
                           </Button>
                        ))}
                     </div>
                  </div>
                  {/* Trend Channel Toggle */}
                  <div className="flex items-center">
                     <Button
                        variant={showTrendChannel ? "default" : "outline"}
                        size="xs"
                        onClick={() => setShowTrendChannel(!showTrendChannel)}
                        className={cn(
                           "h-6 px-2 text-xs",
                           showTrendChannel
                              ? "bg-blue-400 text-white"
                              : "bg-gray-700 text-gray-300 hover:bg-gray-600"
                        )}
                     >
                        {showTrendChannel ? t("hideTrendChannel") : t("showTrendChannel")}
                     </Button>
                  </div>
               </div>
            </div>
         </div>

         {/* Chart Container - Always render with ref to allow chart initialization */}
         <div
            ref={chartContainerRef}
            className="w-full relative"
            style={{ height: `${height}px`, minHeight: `${height}px` }}
         >
            {isLoading && (
               <div className="absolute inset-0 flex items-center justify-center bg-gray-900/80 z-10">
                  <p className="text-gray-400">{t("loading")}</p>
               </div>
            )}
            {!isLoading && (!chartData || chartData.length === 0) && (
               <div className="absolute inset-0 flex items-center justify-center bg-gray-900 z-10">
                  <p className="text-gray-400">{t("noData")}</p>
               </div>
            )}
         </div>
      </div>
   );
});

export default TradingViewChart;
